import { useEffect, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { Video, MapPin, Clock, Heart, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import { useWarningPopup } from "@/contexts/WarningPopupContext";
import FAQSchema from "@/components/FAQSchema";
import SEOHead from "@/components/SEOHead";
import { useInView } from "@/hooks/useInView";

// Lazy load heavy components
const TestimonialsCarousel = lazy(() => import("@/components/TestimonialsCarousel"));

const faqData = [
  {
    question: "O que é psicanálise?",
    answer: "A psicanálise é uma abordagem terapêutica que busca compreender os processos inconscientes que influenciam nossos pensamentos, emoções e comportamentos. Através da escuta atenta e do diálogo, trabalhamos juntas para identificar padrões, elaborar conflitos e promover autoconhecimento profundo."
  },
  {
    question: "Qual a diferença entre psicoterapia e psicanálise?",
    answer: "A psicoterapia é um termo amplo que engloba diversas abordagens de tratamento psicológico. A psicanálise é uma dessas abordagens, com foco especial no inconsciente e na história de vida da pessoa. Na Entrelaces, utilizamos a escuta psicanalítica para oferecer um espaço de reflexão e transformação."
  },
  {
    question: "Quanto tempo dura uma sessão?",
    answer: "Cada sessão tem duração de 45 minutos. Esse tempo é dedicado exclusivamente a você, suas questões e seu processo de autoconhecimento."
  },
  {
    question: "Com que frequência devo fazer terapia?",
    answer: "Geralmente, recomendamos sessões semanais para manter a continuidade do processo terapêutico. No entanto, a frequência pode ser ajustada de acordo com suas necessidades e disponibilidade."
  },
  {
    question: "As sessões online são tão efetivas quanto as presenciais?",
    answer: "Sim! A terapia online é tão efetiva quanto a presencial. O mais importante é o vínculo terapêutico e a qualidade da escuta. Muitas pessoas preferem o formato online pela praticidade e conforto de estar em seu próprio espaço."
  },
  {
    question: "Como funciona o sigilo profissional?",
    answer: "O sigilo é um pilar fundamental da nossa prática. Tudo o que é compartilhado nas sessões é estritamente confidencial, protegido pelo Código de Ética dos Psicólogos. Você pode se sentir segura para falar sobre qualquer assunto."
  },
  {
    question: "Preciso ter um problema grave para fazer terapia?",
    answer: "Não! A terapia não é apenas para momentos de crise. É um espaço para autoconhecimento, desenvolvimento pessoal e cuidado com a saúde mental. Muitas pessoas buscam terapia para se conhecerem melhor e viverem de forma mais plena."
  }
];

interface IndexProps {
  section?: string;
}

// Reusable animated section wrapper using CSS + IntersectionObserver
const AnimatedSection = ({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) => {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-600 ease-out ${className}`}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(20px)",
        transitionDelay: `${delay}ms`,
        transitionDuration: "600ms",
      }}
    >
      {children}
    </div>
  );
};

const WhatsAppSVG = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const WhatsAppSVGLarge = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Index = ({ section }: IndexProps) => {
  const location = useLocation();
  const warningPopup = useWarningPopup();

  const getSEOConfig = () => {
    switch (location.pathname) {
      case "/aviso":
        return {
          title: "Informações Importantes | Entrelaces Psicologia",
          description: "Informações importantes sobre nossos atendimentos. A Entrelaces Psicologia trabalha exclusivamente com atendimentos particulares. Não aceitamos convênios ou planos de saúde.",
          canonicalUrl: "https://entrelacespsicologia.com.br/aviso",
        };
      case "/sobre":
        return {
          title: "Sobre Nós | Entrelaces Psicologia - Psicólogas em Recife",
          description: "Conheça a equipe Entrelaces Psicologia. Gleine Lima e Priscilla Silva, psicólogas clínicas especializadas em psicanálise, oferecendo escuta acolhedora em Recife.",
          canonicalUrl: "https://entrelacespsicologia.com.br/sobre",
        };
      case "/servicos":
        return {
          title: "Serviços | Entrelaces Psicologia - Psicoterapia em Recife",
          description: "Psicoterapia psicanalítica para mulheres. Atendimento online via Google Meet ou presencial em Casa Forte, Recife. Sessões de 45 minutos com sigilo total.",
          canonicalUrl: "https://entrelacespsicologia.com.br/servicos",
        };
      case "/localizacao":
        return {
          title: "Localização | Entrelaces Psicologia - Casa Forte, Recife",
          description: "Encontre a Entrelaces Psicologia em Casa Forte, Recife. Praça de Casa Forte, 381 – Empresarial Alcides Fernandes – Sala 503. Atendimento presencial ou online.",
          canonicalUrl: "https://entrelacespsicologia.com.br/localizacao",
        };
      default:
        return {
          title: "Entrelaces Psicologia | Psicóloga em Casa Forte, Recife",
          description: "Psicoterapia para mulheres em Recife. Atendimento online ou presencial com sigilo e acolhimento. Agende sua consulta particular.",
          canonicalUrl: "https://entrelacespsicologia.com.br",
        };
    }
  };

  const seoConfig = getSEOConfig();

  useEffect(() => {
    if (section) {
      const element = document.getElementById(section);
      if (element) {
        const headerHeight = 112;
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        setTimeout(() => {
          window.scrollTo({ top: elementPosition - headerHeight, behavior: "smooth" });
        }, 100);
      }
    }
  }, [section]);

  const features = [
    { icon: Heart, title: "Acolhimento", description: "Escuta empática e ambiente seguro para sua jornada terapêutica." },
    { icon: Shield, title: "Sigilo Total", description: "Confidencialidade garantida em todas as sessões." },
    { icon: Video, title: "Online ou Presencial", description: "Flexibilidade para atendimento no formato que preferir." },
    { icon: Clock, title: "Sessões de 45min", description: "Tempo dedicado exclusivamente a você e suas necessidades." },
  ];

  const womenStatements = [
    { icon: Heart, highlight: "Equilíbrio emocional", text: "não é luxo, é necessidade.", gradient: "from-rose-500/20 to-pink-500/20" },
    { icon: Shield, highlight: "Profundidade, ética e cuidado", text: "verdadeiro é o que você merece.", gradient: "from-primary/20 to-sage-dark/20" },
    { icon: Heart, highlight: "Ser compreendida", text: "e não 'consertada'.", gradient: "from-accent/20 to-terracotta/20" },
  ];

  return (
    <Layout>
      <SEOHead title={seoConfig.title} description={seoConfig.description} canonicalUrl={seoConfig.canonicalUrl} />
      <FAQSchema faqs={faqData} />

      {/* Hero Section - NO framer-motion, instant render for LCP */}
      <section id="inicio" className="relative min-h-[calc(100vh-4rem)] md:min-h-[calc(100vh-7rem)] flex items-start gradient-hero overflow-hidden">
        <div className="container relative z-10 py-2 md:py-4">
          <div className="grid lg:grid-cols-2 gap-4 md:gap-6 lg:gap-16 items-center">
            {/* Left side - Hero text */}
            <div className="space-y-3 md:space-y-6 lg:space-y-8 text-center lg:text-left">
              <span className="inline-flex items-center gap-2 px-3 py-1 md:px-4 md:py-2 rounded-full bg-sage-light text-sage-dark text-xs md:text-sm font-medium shadow-sm">
                <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-primary animate-pulse" />
                Psicoterapia em Recife
              </span>

              <div className="space-y-2 md:space-y-4">
                <h1 className="font-serif text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-foreground leading-[1.1]">
                  Psicóloga em Recife<br />
                  <span className="text-primary">Sobrecarga Mental</span>
                </h1>
                <p className="text-sm sm:text-base md:text-xl text-muted-foreground max-w-lg leading-relaxed mx-auto lg:mx-0">
                  Psicoterapia para mulheres, online ou presencial, com <span className="text-foreground font-medium">sigilo</span> e <span className="text-foreground font-medium">acolhimento</span>.
                </p>
              </div>

              {/* Badges - hidden on mobile to save space */}
              <div className="hidden md:flex flex-row flex-wrap justify-center lg:justify-start gap-3">
                <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-card border border-border shadow-sm">
                  <div className="w-8 h-8 rounded-full bg-sage-light flex items-center justify-center">
                    <Video className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-sm font-medium text-foreground">Online via Google Meet</span>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-card border border-border shadow-sm">
                  <div className="w-8 h-8 rounded-full bg-sage-light flex items-center justify-center">
                    <Clock className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-sm font-medium text-foreground">Sessões de 45 minutos</span>
                </div>
              </div>

              {/* Address - hidden on mobile */}
              <a
                href="https://www.google.com/maps/search/?api=1&query=Praça+de+Casa+Forte,+381+Empresarial+Alcides+Fernandes+Sala+503+Casa+Forte+Recife+PE"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:inline-flex items-start gap-3 px-5 py-4 rounded-2xl bg-gradient-to-br from-sage-light/50 to-card border border-primary/20 shadow-sm max-w-md mx-auto lg:mx-0 hover:border-primary/40 hover:shadow-md transition-all duration-300 cursor-pointer group"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">Atendimento Presencial</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Praça de Casa Forte, 381 – Empresarial Alcides Fernandes – Sala 503
                  </p>
                </div>
              </a>

              {/* WhatsApp CTA - always visible */}
              <div className="flex flex-col justify-center lg:justify-start gap-1.5 md:gap-2">
                <div className="flex flex-col items-center lg:items-start">
                  <button
                    onClick={warningPopup.open}
                    className="inline-flex items-center justify-center gap-2 md:gap-3 px-5 py-2.5 md:px-8 md:py-4 bg-[#15692E] hover:bg-[#125A27] text-white font-semibold rounded-full shadow-lg transition-all duration-300 hover:scale-105 text-sm md:text-base w-full sm:w-auto"
                  >
                    <WhatsAppSVGLarge />
                    Agendar pelo WhatsApp
                  </button>
                  <span className="text-[10px] md:text-xs text-muted-foreground mt-1 text-center w-full sm:w-auto">
                    Atendimento particular | Retorno em horário comercial
                  </span>
                </div>
              </div>

              {/* Mobile compact info */}
              <div className="flex md:hidden justify-center gap-4 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Video className="w-3.5 h-3.5 text-primary" />
                  Online
                </span>
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-primary" />
                  Casa Forte
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-primary" />
                  45 min
                </span>
              </div>

              {/* Testimonials - visible on all screens */}
              <div>
                <Suspense fallback={<div className="min-h-[80px]" />}>
                  <TestimonialsCarousel />
                </Suspense>
              </div>
            </div>

            {/* Right side - Photos - NO motion wrapper */}
            <div className="relative grid grid-cols-2 gap-3 md:gap-6">
              {/* Gleine Lima */}
              <div className="flex flex-col card-therapeutic overflow-hidden">
                <div className="relative aspect-[3/4] w-full overflow-hidden bg-muted">
                  <img
                    src="/images/psicologa-2-sm.webp"
                    srcSet="/images/psicologa-2-sm.webp 420w, /images/psicologa-2.webp 864w"
                    sizes="(max-width: 768px) 42vw, 292px"
                    alt="Gleine Lima - Psicóloga Clínica"
                    className="w-full h-full object-cover object-top"
                    width={292}
                    height={389}
                    fetchPriority="high"
                  />
                </div>
                <div className="text-center p-2 md:p-4 flex-1 flex flex-col">
                  <p className="font-serif text-sm md:text-lg font-bold text-foreground">Gleine Lima</p>
                  <p className="text-xs md:text-sm text-primary font-medium">Psicóloga Clínica</p>
                  <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 md:mt-1">CRP: 02/12277</p>
                  <p className="hidden md:block text-xs text-muted-foreground mt-2 leading-relaxed flex-1">
                    Especialista em Saúde Mental, Psicologia Clínica e Membro da Intersecção Psicanalítica do Brasil e da Casa Freud-Lacan.
                  </p>
                </div>
              </div>

              {/* Priscilla Silva */}
              <div className="flex flex-col card-therapeutic overflow-hidden">
                <div className="relative aspect-[3/4] w-full overflow-hidden bg-muted">
                  <img
                    src="/images/psicologa-1-sm.webp"
                    srcSet="/images/psicologa-1-sm.webp 420w, /images/psicologa-1.webp 864w"
                    sizes="(max-width: 768px) 42vw, 292px"
                    alt="Priscilla Silva - Psicóloga Clínica"
                    className="w-full h-full object-cover object-top"
                    width={292}
                    height={389}
                    fetchPriority="high"
                  />
                </div>
                <div className="text-center p-2 md:p-4 flex-1 flex flex-col">
                  <p className="font-serif text-sm md:text-lg font-bold text-foreground">Priscilla Silva</p>
                  <p className="text-xs md:text-sm text-primary font-medium">Psicóloga Clínica</p>
                  <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 md:mt-1">CRP: 02/14207</p>
                  <p className="hidden md:block text-xs text-muted-foreground mt-2 leading-relaxed flex-1">
                    Especialista em Psicologia Clínica de Orientação Psicanalítica pelo CPPL e Unicamp.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - CSS animations instead of framer-motion */}
      <section id="servicos" className="py-16 md:py-24 bg-secondary">
        <div className="container">
          <AnimatedSection className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4">
              Por que escolher a Entrelaces?
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mx-auto">
              Somos um espaço de escuta acolhedora em Recife, dedicado à psicanálise exatamente para mulheres como você.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {womenStatements.map((statement, index) => (
              <AnimatedSection key={index} delay={index * 200}>
                <div className="relative group cursor-pointer h-full">
                  <div className={`absolute inset-0 bg-gradient-to-br ${statement.gradient} rounded-3xl blur-2xl opacity-0 group-hover:opacity-70 transition-all duration-500 scale-105`} />
                  <div className="relative bg-gradient-to-br from-card via-card to-secondary/50 border border-border/50 rounded-3xl p-8 md:p-10 text-center hover:border-primary/40 transition-all duration-500 h-full flex flex-col justify-center items-center gap-4 shadow-sm hover:shadow-xl group-hover:-translate-y-2">
                    <div className="w-12 h-1 rounded-full bg-gradient-to-r from-primary to-accent mb-2" />
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-sage-light to-secondary flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <statement.icon className="w-8 h-8 text-primary" />
                    </div>
                    <div className="space-y-2">
                      <p className="font-serif text-xl md:text-2xl font-bold text-foreground leading-tight">{statement.highlight}</p>
                      <p className="text-base md:text-lg text-muted-foreground leading-relaxed">{statement.text}</p>
                    </div>
                    <div className="flex gap-1.5 mt-2">
                      <div className="w-2 h-2 rounded-full bg-primary/30" />
                      <div className="w-2 h-2 rounded-full bg-primary/50" />
                      <div className="w-2 h-2 rounded-full bg-primary/30" />
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {features.map((feature, index) => (
              <AnimatedSection key={feature.title} delay={index * 100}>
                <div className="card-therapeutic p-6 text-center hover:shadow-soft transition-shadow duration-300">
                  <div className="w-14 h-14 rounded-full bg-sage-light flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection delay={400} className="text-center">
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={warningPopup.open}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold rounded-full shadow-lg transition-all duration-300 hover:scale-105 text-lg"
              >
                <WhatsAppSVGLarge />
                Agendar pelo WhatsApp
              </button>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Praça+de+Casa+Forte,+381+-+Sala+503+-+Recife+PE"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-full shadow-lg transition-all duration-300 hover:scale-105 text-lg"
              >
                <MapPin className="w-6 h-6" />
                Ver localização
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="sobre" className="py-16 md:py-24">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <AnimatedSection>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground">
                  Aqui, sua história encontra escuta.
                </h2>
              </AnimatedSection>

              <div className="space-y-6">
                <AnimatedSection delay={100}>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    Sabe aquelas ansiedades que você carrega?<br />
                    <span className="text-foreground font-medium">Não vieram do nada.</span>
                  </p>
                </AnimatedSection>
                <AnimatedSection delay={200}>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    Aquele vazio que você sente quando tudo deveria estar bem?<br />
                    <span className="text-foreground font-medium">Tem origem.</span>
                  </p>
                </AnimatedSection>
                <AnimatedSection delay={300}>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    Aquela dificuldade em se permitir descansar, em confiar, em se amar?<br />
                    <span className="text-foreground font-medium">Ela tem raízes muito mais profundas.</span>
                  </p>
                </AnimatedSection>
                <AnimatedSection delay={400}>
                  <p className="text-lg text-primary/90 italic leading-relaxed border-l-4 border-primary/30 pl-4">
                    E tentar resolver tudo rápido é como colocar um band-aid em uma ferida que precisa ser compreendida.
                  </p>
                </AnimatedSection>
                <AnimatedSection delay={500}>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    Você não precisa correr. Você não precisa ter respostas prontas. Você só precisa estar disposta a se conhecer. E nós estamos aqui para acolher cada passo dessa jornada.
                  </p>
                </AnimatedSection>
                <AnimatedSection delay={600}>
                  <p className="text-lg text-foreground font-medium leading-relaxed">
                    Você não sai daqui 'julgada'. Você sai daqui conhecendo a si mesma. E isso muda tudo.
                  </p>
                </AnimatedSection>
                <AnimatedSection delay={700}>
                  <p className="font-serif text-xl md:text-2xl text-primary font-bold leading-relaxed">
                    Porque a verdade é essa: sua história não cabe em respostas prontas.
                  </p>
                </AnimatedSection>
              </div>

              <AnimatedSection delay={800}>
                <div className="flex flex-wrap gap-3">
                  <Button variant="hero" size="lg" onClick={warningPopup.open}>
                    <WhatsAppSVG />
                    Agendar pelo WhatsApp
                  </Button>
                  <Button variant="outline-sage" size="lg" asChild>
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=Praça+de+Casa+Forte,+381+-+Sala+503+-+Recife+PE"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MapPin className="w-5 h-5" />
                      Ver localização
                    </a>
                  </Button>
                </div>
              </AnimatedSection>
            </div>

            <AnimatedSection>
              <div className="flex flex-col gap-4">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-card">
                  <img
                    src="/images/team-entrelaces-sm.webp"
                    srcSet="/images/team-entrelaces-sm.webp 620w, /images/team-entrelaces.webp 1024w"
                    sizes="(max-width: 1024px) 90vw, 620px"
                    alt="Equipe Entrelaces Psicologia"
                    className="w-full h-full object-cover"
                    width={620}
                    height={620}
                    loading="lazy"
                    fetchPriority="low"
                  />
                </div>
                <div className="bg-card rounded-xl p-4 md:p-6 shadow-card border border-border">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-sage-light flex items-center justify-center">
                      <Heart className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-serif text-xl md:text-2xl font-bold text-foreground">+500</p>
                      <p className="text-xs md:text-sm text-muted-foreground">Pacientes atendidos</p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-secondary">
        <div className="container">
          <AnimatedSection className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4">
              Perguntas Frequentes
            </h2>
            <p className="text-muted-foreground text-lg">
              Tire suas dúvidas sobre psicoterapia e psicanálise
            </p>
          </AnimatedSection>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqData.map((faq, index) => (
              <AnimatedSection key={index} delay={index * 100}>
                <details className="bg-card rounded-2xl border border-border overflow-hidden hover:border-primary/30 transition-colors duration-300 group">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <h3 className="font-serif text-lg font-semibold text-foreground pr-4">{faq.question}</h3>
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-sage-light flex items-center justify-center group-open:rotate-45 transition-transform duration-300">
                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                  </summary>
                  <div className="px-6 pb-6 pt-0">
                    <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection delay={500} className="text-center mt-12">
            <p className="text-muted-foreground mb-4">Ainda tem dúvidas? Fale conosco!</p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={warningPopup.open}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#20BD5A] text-white font-medium rounded-full shadow-lg transition-all duration-300 hover:scale-105"
              >
                <WhatsAppSVG />
                Agendar pelo WhatsApp
              </button>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Praça+de+Casa+Forte,+381+-+Sala+503+-+Recife+PE"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-full shadow-lg transition-all duration-300 hover:scale-105"
              >
                <MapPin className="w-5 h-5" />
                Ver localização
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      <section id="localizacao" className="py-16 md:py-24 bg-primary">
        <div className="container">
          <AnimatedSection className="text-center max-w-2xl mx-auto space-y-8">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary-foreground">
              Pronto para começar sua jornada?
            </h2>
            <p className="text-primary-foreground/80 text-lg">
              Dê o primeiro passo em direção ao seu bem-estar. Agende sua consulta agora mesmo e comece a cuidar de você.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                variant="outline"
                size="xl"
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 border-0"
                onClick={warningPopup.open}
              >
                <WhatsAppSVG />
                Agendar pelo WhatsApp
              </Button>
              <Button
                variant="outline"
                size="xl"
                className="bg-transparent text-primary-foreground hover:bg-primary-foreground/10 border-2 border-primary-foreground"
                asChild
              >
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Praça+de+Casa+Forte,+381+-+Sala+503+-+Recife+PE"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MapPin className="w-5 h-5" />
                  Ver localização
                </a>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
